import queryString from 'query-string';
import { ExportTemplateListRequest } from 'src/adminPanel/components/pages/it/marketing-documents/DocumentValueListTable';
import MarketingDocumentQueryParams from 'src/adminPanel/components/pages/it/marketing-documents/MarketingDocumentQueryParams';
import { TextBlockResponse } from 'src/adminPanel/components/pages/it/marketing-text-blocks/ComparisonTable';
import { TextBlockDownloadRequestI } from 'src/adminPanel/components/pages/it/marketing-text-blocks/MarketingTextBlocksPage';
import { TemplateImportRequest } from 'src/adminPanel/components/pages/it/marketing-text-blocks/TemplateFileImporter';
import NewLanguageBlock from 'src/adminPanel/entities/templateAdmin/NewLanguageBlock';
import callApi, { CallApiResult } from 'src/api';
import { PageableQueryParams } from 'src/entities/basic-types/Pageable';
import DocumentRegions from 'src/entities/templates/DocumentRegionsResponse';
import MarketingDocument from 'src/entities/templates/MarketingDocument';

export const getLocalizedTemplateContent = (templateKey: string): Promise<CallApiResult> => {
    return callApi('/template/v3/template/' + templateKey);
};

export const createNewLanguageblock = (languageblock: NewLanguageBlock): Promise<CallApiResult> => {
    return callApi('/template/v3/admin/languageblock/create', { method: 'POST', data: languageblock });
};

export const editLanguageblockTranslation = (
    normalizedName: string,
    languageIso2: string,
    text: string
): Promise<CallApiResult> => {
    const endpoint = `/template/v3/admin/languageblock/${normalizedName}/translation/${languageIso2}`;
    return callApi(endpoint, { method: 'PUT', headers: { 'content-type': 'text/plain' }, data: text });
};

/* Marketing Text Blocks */

/* Initial */
export const getAllLanguageBlocks = (pageable: PageableQueryParams): Promise<CallApiResult> => {
    let query = queryString.stringify({ ...pageable }, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?' + query;
    const endpoint = `/template/v3/admin/languageblock/all${query}`;
    return callApi(endpoint);
};

/* Filter Key Search */
export const getLanguageBlocksByFilter = (
    pageable: PageableQueryParams,
    filterParams: string
): Promise<CallApiResult> => {
    let query = queryString.stringify({ ...pageable }, { skipEmpty: true, skipNull: true });
    if (query !== '') query = '?filterKey=textKey&filterValue=' + filterParams + '&' + query;
    const endpoint = `/template/v3/admin/languageblock/all${query}`;
    return callApi(endpoint);
};

/* Full Text Search */
export const getLanguageBlocksByQuery = (pageable: PageableQueryParams, queryParam: string): Promise<CallApiResult> => {
    const query = queryString.stringify({ ...pageable }, { skipEmpty: true, skipNull: true });
    if (queryParam !== '') queryParam += '?' + query;
    const endpoint = `/template/v3/admin/languageblock/query/${queryParam}`;
    return callApi(endpoint);
};

/* Get all translations by language block key */
export const getTranslationsByLanguageBlockKey = (languageBlockKey: string): Promise<CallApiResult> => {
    const endPoint = `/template/v3/admin/languageblock/${languageBlockKey}`;
    return callApi(endPoint);
};

/* Get languages (iso2) */
export const getLanguages = (): Promise<CallApiResult> => {
    const endpoint = '/template/v3/admin/languageblock/languages';
    return callApi(endpoint);
};

/* Add new languages */
export const addNewLanguages = (
    languageBlockKey: string,
    languageIso2: string,
    content: string
): Promise<CallApiResult> => {
    return callApi(`/template/v3/admin/languageblock/${languageBlockKey}/translation/${languageIso2}`, {
        headers: { 'Content-Type': 'text/plain' },
        method: 'PUT',
        data: content.length === 0 ? languageBlockKey : content,
    });
};

/* Update description */
export const updateLanguageBlockDescription = (
    languageBlockKey: string,
    description?: string
): Promise<CallApiResult> => {
    return callApi(`/template/v3/admin/languageblock/${languageBlockKey}`, {
        method: 'PATCH',
        data: { description },
    });
};

/* Update translation */
export const updateTranslation = (
    languageBlockKey: string,
    languageIso2: string,
    translationText: string
): Promise<CallApiResult> => {
    return callApi(`/template/v3/admin/languageblock/${languageBlockKey}/translation/${languageIso2}`, {
        headers: { 'Content-Type': 'text/plain' },
        method: 'PUT',
        data: translationText,
    });
};

/* Delete Translation */
export const deleteTranslation = (languageBlockKey: string, languageIso2: string) => {
    return callApi(`/template/v3/admin/languageblock/${languageBlockKey}/language/${languageIso2}`, {
        headers: { 'Content-Type': 'text/plain' },
        method: 'DELETE',
    });
};

/* Export selected templates */
export const downloadTextBlocks = (textKeys: TextBlockDownloadRequestI[]): Promise<CallApiResult> => {
    return callApi('/template/v3/admin/languageblock/export', { method: 'POST', responseType: 'blob', data: textKeys });
};

/* Import Language blocks file for the preview */
export const importLanguageBlocks = (uploadFile: FormData): Promise<CallApiResult<TextBlockResponse>> => {
    return callApi('/template/v3/admin/languageblock/import', {
        method: 'POST',
        data: uploadFile,
    });
};

/* Import Marketing Documents file for the preview */
export const importRegionBlocks = (uploadFile: FormData): Promise<CallApiResult<TextBlockResponse>> => {
    return callApi('/template/v3/admin/regionblock/import', {
        method: 'POST',
        data: uploadFile,
    });
};

/* Save viewed Language templates file */
export const saveImportedLanguageTemplates = (
    importedData: TemplateImportRequest[]
): Promise<CallApiResult<TextBlockResponse>> => {
    return callApi('/template/v3/admin/languageblock/saveImport', {
        method: 'POST',
        data: importedData,
    });
};

/* Get all region blocks */
export const getAllRegionBlocks = (params: MarketingDocumentQueryParams): Promise<CallApiResult> => {
    let page = queryString.stringify({ ...params.pageable }, { skipEmpty: true, skipNull: true });
    if (page !== '') page = '?' + page;
    let searchKey = '';

    if (params.query?.filterKey !== '' && params.query?.filterValue !== '') {
        searchKey = queryString.stringify(params.query, { skipEmpty: true, skipNull: true });
        if (page === '' && searchKey !== '') {
            searchKey = '?';
        } else {
            searchKey = '&' + searchKey;
        }
    }
    const endpoint = `/template/v3/admin/regionblock/all${page}${searchKey}`;
    return callApi(endpoint);
};

/* Get translations for a region block */
export const getDocumentRegionsByLanguageBlockKey = (textKey: string): Promise<CallApiResult<DocumentRegions>> => {
    return callApi(`template/v3/admin/regionblock/${textKey}`);
};

/* Save Region Document Note: PUT -> /template/v3/admin/regionblock/{regionBlockKey}/note; Body -> String note*/
export const updateRegionDocumentNote = (regionBlockKey: string, note?: string): Promise<CallApiResult> => {
    return callApi(`/template/v3/admin/regionblock/${regionBlockKey}/note`, {
        method: 'PUT',
        data: { note },
    });
};

/* Get countries (iso2) */
export const getCountries = (): Promise<CallApiResult> => {
    const endpoint = '/template/v3/admin/regionblock/regions';
    return callApi(endpoint);
};

/* Add new counties: */
export const addNewRegions = (regionBlockKey: string, regionIso2: string): Promise<CallApiResult> => {
    return callApi(`/template/v3/admin/regionblock/${regionBlockKey}/region/${regionIso2}`, {
        headers: { 'Content-Type': 'text/plain' },
        method: 'PUT',
    });
};

/* Delete Region */
export const deleteRegion = (regionBlockKey: string, regionIso2: string) => {
    return callApi(`/template/v3/admin/regionblock/${regionBlockKey}/region/${regionIso2}`, {
        headers: { 'Content-Type': 'text/plain' },
        method: 'DELETE',
    });
};

/* Update Region */
export const updateRegion = (regionBlockKey: string, regionIso2: string, content: string): Promise<CallApiResult> => {
    return callApi(`/template/v3/admin/regionblock/${regionBlockKey}/content/${regionIso2}`, {
        headers: { 'Content-Type': 'text/plain' },
        method: 'PUT',
        data: content,
    });
};

/* Create new Marketing-Document */
export const createNewRegionBlock = (marketingDocument: MarketingDocument): Promise<CallApiResult> => {
    return callApi('/template/v3/admin/regionblock/create', { method: 'POST', data: marketingDocument });
};

/* Export document blocks */
export const downloadRegionBlocks = (textBlocksRequestDtoList: ExportTemplateListRequest[]): Promise<CallApiResult> => {
    return callApi('/template/v3/admin/regionblock/export', {
        method: 'POST',
        responseType: 'blob',
        data: textBlocksRequestDtoList,
    });
};

/* Import document blocks */
export const ImportRegionBlocks = (uploadFile: FormData): Promise<CallApiResult<TextBlockResponse>> => {
    return callApi('/template/v3/admin/regionblock/import', {
        method: 'POST',
        data: uploadFile,
    });
};

/* Save viewed Region templates file */
export const saveImportedRegionTemplates = (
    regionBlockKey: string,
    importedData: TemplateImportRequest[]
): Promise<CallApiResult<TextBlockResponse>> => {
    return callApi(`/template/v3/admin/regionblock/saveImport/${regionBlockKey}`, {
        method: 'POST',
        data: importedData,
    });
};
